import { SearchQueryFieldConstraintHierarchyNode } from "types/generated";
import { HierarchyFilterOptionsMap, FilterItemOptionValue } from "components/Filters/types";

export const parseHierarchyNode = (str: string): SearchQueryFieldConstraintHierarchyNode | null => {
  try {
    return JSON.parse(str) as SearchQueryFieldConstraintHierarchyNode;
  } catch {
    return null;
  }
};

const stringifyHierarchyNode = (node: SearchQueryFieldConstraintHierarchyNode): string =>
  JSON.stringify(node);

const getAllDescendantIds = (id: string, optionsMap: HierarchyFilterOptionsMap): string[] => {
  const item = optionsMap.get(id);
  if (!item || !item.children || item.children.length === 0) return [];
  return item.children.reduce<string[]>(
    (acc, childId) => [...acc, childId, ...getAllDescendantIds(childId, optionsMap)],
    []
  );
};

// Check if the item is explicitly selected with either includeDescendants: true or false
const isItemIdSelected = (selectedId: string, selectedValues: Set<string>): boolean => {
  return (
    selectedValues.has(stringifyHierarchyNode({ equals: selectedId, includeDescendants: true })) ||
    selectedValues.has(stringifyHierarchyNode({ equals: selectedId, includeDescendants: false }))
  );
};

// Check if the item is selected by its parent with includeDescendants: true
export const isItemSelectedByParent = (
  selectedId: string,
  selectedValues: Set<string>,
  optionsMap: HierarchyFilterOptionsMap
): boolean => {
  // Check all ancestors up the hierarchy
  let currentId = selectedId;
  let parentId = optionsMap.get(currentId)?.parentId;

  while (parentId) {
    // Check if this ancestor is selected with includeDescendants: true
    const parentSelection = stringifyHierarchyNode({
      equals: parentId,
      includeDescendants: true,
    });

    if (selectedValues.has(parentSelection)) {
      return true;
    }

    // Move up to the next ancestor
    currentId = parentId;
    parentId = optionsMap.get(currentId)?.parentId;
  }

  return false;
};

const appearsSelected = (
  selectedId: string,
  selectedValues: Set<string>,
  optionsMap: HierarchyFilterOptionsMap
): boolean => {
  return (
    isItemIdSelected(selectedId, selectedValues) ||
    isItemSelectedByParent(selectedId, selectedValues, optionsMap)
  );
};

export const getSelectionState = (
  itemId: string,
  selectedValues: Set<FilterItemOptionValue>,
  optionsMap: HierarchyFilterOptionsMap
): "unselected" | "partial" | "selected" => {
  // Convert selectedValues to Set<string> for helper functions
  const selectedValuesAsStrings = new Set(Array.from(selectedValues).map(String));
  const itemAppearsSelected = appearsSelected(itemId, selectedValuesAsStrings, optionsMap);

  if (!itemAppearsSelected) {
    return "unselected";
  }

  const allDescendants = getAllDescendantIds(itemId, optionsMap);
  if (allDescendants.length === 0) {
    // This is a leaf node, so if it appears selected, it's fully selected
    return "selected";
  }

  if (
    selectedValuesAsStrings.has(
      stringifyHierarchyNode({ equals: itemId, includeDescendants: false })
    )
  ) {
    // Item is selected but doesn't include descendants, so it's partial
    return "partial";
  }

  // Parent is not explicitly selected, but if it appears selected (by parent), it should show as selected
  // This handles the case where a child item with children is selected by its parent
  return "selected";
};

const removeExplicitSelection = (
  selectedId: string,
  includeDescendants: boolean,
  selectedValues: Set<string>,
  optionsMap: HierarchyFilterOptionsMap
): Set<string> => {
  const newSelected = new Set(selectedValues);

  // Always remove both includeDescendants: true and false versions of the same item
  newSelected.delete(stringifyHierarchyNode({ equals: selectedId, includeDescendants: true }));
  newSelected.delete(stringifyHierarchyNode({ equals: selectedId, includeDescendants: false }));

  // If includeDescendants is true, remove all descendants
  if (includeDescendants) {
    const descendants = getAllDescendantIds(selectedId, optionsMap);
    descendants.forEach((descId) => {
      newSelected.delete(stringifyHierarchyNode({ equals: descId, includeDescendants: true }));
      newSelected.delete(stringifyHierarchyNode({ equals: descId, includeDescendants: false }));
    });
  }

  return newSelected;
};

export const selectItemAndUnselectAllChildren = (
  selectedId: string,
  selectedValues: Set<string>,
  optionsMap: HierarchyFilterOptionsMap
): Set<string> => {
  const selectedValue = stringifyHierarchyNode({ equals: selectedId, includeDescendants: false });
  const selectedValueWithDescendants = stringifyHierarchyNode({
    equals: selectedId,
    includeDescendants: true,
  });

  const newSelected = new Set(selectedValues);
  newSelected.add(selectedValue);
  newSelected.delete(selectedValueWithDescendants);

  const descendants = getAllDescendantIds(selectedId, optionsMap);
  descendants.forEach((descId) => {
    newSelected.delete(stringifyHierarchyNode({ equals: descId, includeDescendants: true }));
    newSelected.delete(stringifyHierarchyNode({ equals: descId, includeDescendants: false }));
  });

  return newSelected;
};

// Unselect the item and its parent with includeDescendants: true if the item is explicitly selected
export const removeExplicitSelectionWithParentCheck = (
  selectedId: string,
  includeDescendants: boolean,
  selectedValues: Set<string>,
  optionsMap: HierarchyFilterOptionsMap
): Set<string> => {
  const newSelected = removeExplicitSelection(
    selectedId,
    includeDescendants,
    selectedValues,
    optionsMap
  );

  // Check if we should also unselect the parent
  const parentId = optionsMap.get(selectedId)?.parentId;

  if (parentId) {
    const parentSelection = stringifyHierarchyNode({ equals: parentId, includeDescendants: true });

    // If parent is selected with includeDescendants: true, we should change selection to includeDescendants: false
    // because we just unselected one of its children
    if (newSelected.has(parentSelection)) {
      newSelected.delete(parentSelection);
      newSelected.add(stringifyHierarchyNode({ equals: parentId, includeDescendants: false }));
    }
  }

  return newSelected;
};

// Unselect the item which is selected by its parent with includeDescendants: true
export const excludeItemFromParentSelection = (
  selectedId: string,
  selectedValues: Set<string>,
  optionsMap: HierarchyFilterOptionsMap
): Set<string> => {
  const newSelected = new Set(selectedValues);

  // Find all ancestors
  let currentId = selectedId;
  const ancestors: string[] = [];

  while (currentId) {
    const parentId = optionsMap.get(currentId)?.parentId;
    if (!parentId) break;

    ancestors.push(parentId);
    currentId = parentId;
  }

  if (ancestors.length === 0) {
    // No ancestors with includeDescendants: true found, return unchanged
    return newSelected;
  }

  // Change every ancestor from includeDescendants: true to includeDescendants: false
  ancestors.forEach((ancestorId) => {
    const ancestorSelection = stringifyHierarchyNode({
      equals: ancestorId,
      includeDescendants: true,
    });
    const ancestorWithoutDescendants = stringifyHierarchyNode({
      equals: ancestorId,
      includeDescendants: false,
    });

    const isSelected = appearsSelected(ancestorId, selectedValues, optionsMap);

    if (isSelected) {
      // Remove the ancestor's includeDescendants: true selection
      newSelected.delete(ancestorSelection);
      // Add the ancestor with includeDescendants: false
      newSelected.add(ancestorWithoutDescendants);
    }
  });

  // Now we need to add back all descendants of the topmost ancestor except the excluded item and its descendants
  const topmostAncestor = ancestors[ancestors.length - 1];
  const allDescendants = getAllDescendantIds(topmostAncestor, optionsMap);
  allDescendants.forEach((descId) => {
    // Skip the excluded item and its descendants
    if (descId === selectedId || descId.startsWith(selectedId + "/")) {
      return;
    }

    // Skip any ancestors that we just changed to includeDescendants: false
    if (ancestors.includes(descId)) {
      return;
    }

    const wasInitiallySelected = appearsSelected(descId, selectedValues, optionsMap);

    if (wasInitiallySelected) {
      // Add each descendant with includeDescendants: true
      const descSelection = stringifyHierarchyNode({
        equals: descId,
        includeDescendants: !selectedValues.has(
          stringifyHierarchyNode({ equals: descId, includeDescendants: false })
        ),
      });
      newSelected.add(descSelection);
    }
  });
  return newSelected;
};

export const addSelection = (
  selectedValue: string,
  selectedId: string,
  includeDescendants: boolean,
  selectedValues: Set<string>,
  optionsMap: HierarchyFilterOptionsMap
): Set<string> => {
  const newSelected = new Set(selectedValues);
  newSelected.add(selectedValue);

  // If includeDescendants is true, remove all descendants' explicit selections and the selection with includeDescendants: false if exists
  if (includeDescendants) {
    const selectedValueWithoutDescendants = stringifyHierarchyNode({
      equals: selectedId,
      includeDescendants: false,
    });
    if (newSelected.has(selectedValueWithoutDescendants)) {
      newSelected.delete(selectedValueWithoutDescendants);
    }

    const descendants = getAllDescendantIds(selectedId, optionsMap);
    descendants.forEach((descId) => {
      newSelected.delete(stringifyHierarchyNode({ equals: descId, includeDescendants: true }));
      newSelected.delete(stringifyHierarchyNode({ equals: descId, includeDescendants: false }));
    });
  }

  return newSelected;
};

export const collapseDescendantsToParent = (
  selectedId: string,
  selectedValues: Set<string>,
  optionsMap: HierarchyFilterOptionsMap
): Set<string> => {
  const newSelected = new Set(selectedValues);
  let currentId = selectedId;
  let parentId: string | undefined = optionsMap.get(currentId)?.parentId ?? undefined;

  while (parentId) {
    const descendants = getAllDescendantIds(parentId, optionsMap);
    // Check if all siblings are selected (either explicitly or by parent)
    const allDescendantsSelected = descendants.every((descendantId) => {
      return appearsSelected(descendantId, newSelected, optionsMap);
    });

    if (allDescendantsSelected) {
      // Check if parent is currently selected with includeDescendants: false
      const parentSelectionFalse = stringifyHierarchyNode({
        equals: parentId,
        includeDescendants: false,
      });

      if (newSelected.has(parentSelectionFalse)) {
        // Remove parent with includeDescendants: false
        newSelected.delete(parentSelectionFalse);

        // Remove all siblings' individual selections
        descendants.forEach((descendantId) => {
          newSelected.delete(
            stringifyHierarchyNode({ equals: descendantId, includeDescendants: true })
          );
          newSelected.delete(
            stringifyHierarchyNode({ equals: descendantId, includeDescendants: false })
          );
        });

        // Add parent with includeDescendants: true
        newSelected.add(stringifyHierarchyNode({ equals: parentId, includeDescendants: true }));
      }

      // Move up to check the next level
      currentId = parentId;
      parentId = optionsMap.get(currentId)?.parentId ?? undefined;
    } else {
      break;
    }
  }

  return newSelected;
};
